import LoadingButton from "@mui/lab/LoadingButton"
import { Alert, Link } from "@mui/material"
import Box from "@mui/material/Box"
import CssBaseline from "@mui/material/CssBaseline"
import Grid from "@mui/material/Grid"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useFormik } from "formik"
import { useAppDispatch } from "hooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import authService from "services/authService"
import organizationService from "services/organizationService"
import userService from "services/userService"
import { setCurrentOrganization } from "slices/organizationSlice"
import { setCurrentUser } from "slices/usersSlice"
import { UserRoles } from "types/userRoles"
import * as Yup from "yup"

type LoginForm = {
  username: string
  password: string
}

export function Login() {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const navigate = useNavigate()

  useEffect(() => {
    authService
      .getUser()
      .then((user) => {
        //Navigate user away from /login when already logged in
        if (user !== undefined) {
          navigate("/");
        }
      })
      .catch((err) => {
        console.error(err)
      })
  }, [])

  const initialValues: LoginForm = {
    username: "",
    password: "",
  }

  const loginValidationScheme = Yup.object({
    username: Yup.string().required("Please enter your email"),
    password: Yup.string().required("Please enter your password"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: loginValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)
      setError(undefined)
      authService
        .login(values.username, values.password)
        .then((res) => {
          if (res === "NEW_PASSWORD_REQUIRED") {
            navigate("/sign-up")
          } else if (res === "RESET_PASSWORD") {
            setError("Password reset required for the user")
            navigate("/change-password")
          } else if (res === "DONE") {
            onLoginSuccess()
          } else {
            throw new Error("Failed to login...")
          }
        })
        .catch((err) => {
          console.error(err)
          setError(err.message)
          setIsLoading(false)
        })
    },
  })

  const onLoginSuccess = () => {
    authService
      .getRolesAssignedToUser()
      .then((roles) => {
        if (roles?.includes(UserRoles.HIAB_ADMIN)) {
          navigate("/organization")
          setIsLoading(false)
        } else if (roles?.includes(UserRoles.ORGANIZATION_ADMIN)) {
          userService
            .getBearerUser()
            .then((user) => {
              dispatch(setCurrentUser(user))
              organizationService
                .getOrganization(user.organizationId)
                .then((organization) => {
                  dispatch(setCurrentOrganization(organization))
                  setIsLoading(false)
                  navigate("/")
                })
                .catch((err) => {
                  console.error(err)
                  setIsLoading(false)
                  navigate("/")
                })
            })
            .catch((err) => {
              console.error(err)
              navigate("/")
              setIsLoading(false)
            })
        } else if (roles?.includes(UserRoles.USER)) {
          userService
            .getBearerUser()
            .then((_user) => {
              navigate("/")
              setIsLoading(false)
            })
            .catch((err) => {
              console.error(err)
              navigate("/")
              setIsLoading(false)
            })
        }
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
      })
  }
  
  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={8}
        sx={{
          backgroundImage:
            "url(https://hiabstorageproduction.blob.core.windows.net/product-media/632670.jpg)",
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) =>
            t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900],
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={4} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            color="secondary"
            gutterBottom
            paddingTop={8}
            paddingBottom={12}
          >
            HIAB TRAINING SIMULATOR
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  id="username"
                  label="Email"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  id="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                />
              </Grid>
            </Grid>
            {error !== undefined && (
              <Alert data-testid="alert" severity="error">
                Wrong email or password
              </Alert>
            )}
            <Box sx={{ m: 0, position: "relative" }}>
              <LoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loading={isLoading}
              >
                Sign In
              </LoadingButton>
              <Grid container>
                <Grid item xs>
                  <Link href="/forgot-password" variant="body2">
                    Forgot password?
                  </Link>
                  <Link
                    sx={{ marginLeft: 2 }}
                    href="https://support.hiabtrainingsimulator.com/"
                    target="_blank"
                    variant="body2"
                  >
                    Support
                  </Link>
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                bottom={40}
                position="fixed"
                marginLeft={17}
              >
                {"Copyright © "}
                <Link color="inherit" href="https://hiab.com/">
                  Hiab
                </Link>
                {new Date().getFullYear()}
              </Typography>
            </Box>
          </form>
        </Box>
      </Grid>
    </Grid>
  )
}
